import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import filterMenuElements from 'commons/src/components/menu/FilterMenuElements';
import styles from 'commons/src/components/menu/IconStatusDotOverlay.module.scss';
import NavigationSideBar from 'commons/src/components/menu/NavigationSideBar';
import { roleRestrictions } from 'commons/src/constants';
import AirthingsHorizontalLogo from 'commons/src/img/logos/Airthings_Logo_Horizontal_Dark_Grey.png';
import { FeatureToggleEnum, GroupType, RequiredRoleLevel } from 'commons/src/models/commonEnums';
import { NavigationItem } from 'commons/src/models/menuModels';
import { fetchOrganizationHealthStatusSummary } from '../../actions/healthStatusActions';
import { paths } from '../../constants';
import logo from '../../img/logos/airthings_for_business_logo.svg';
import simpleLogo from '../../img/logos/airthings_simple_logo.svg';
import { Store } from '../../reducers';

type ParentProps = {
    publicPaths: (string | undefined)[];
};

type Props = ParentProps;

export const NavigationComponent = ({ publicPaths }: Props): React.ReactElement => {
    const managedByOrganizationDetails = useSelector((store: Store) => store.userSettings.managedByOrganizationDetails);
    const selectedGroup = useSelector((store: Store) => store.userSettings.selectedGroup);
    const partnerLogo = useSelector((store: Store) => store.organizationProperties.logoImage);
    const featureToggles = useSelector((store: Store) => store.userSettings.featureToggles);
    const isLoggedIn = useSelector((store: Store) => store.login.isLoggedIn);
    let currentPath = useLocation().pathname;

    const dispatch = useDispatch();

    useEffect(() => {
        if (isLoggedIn && selectedGroup) {
            dispatch(fetchOrganizationHealthStatusSummary());
        }
    }, [isLoggedIn, selectedGroup]);

    const summary = useSelector((store: Store) => store.buildingsHealthStatus.healthStatusSummary);
    const summaryRequest = useSelector((state: Store) => state.requests.FETCH_ORGANIZATION_HEALTH_STATUS_SUMMARY);

    const extraClass = useMemo(() => {
        if (summaryRequest.loading && !summary) {
            return classNames(styles.dotStatus, styles.dotStatusWait);
        }
        const hasIssues = (summary?.offlineHubs ?? 0) + (summary?.offlineDevices ?? 0) > 0;
        return classNames(styles.dotStatus, hasIssues ? styles.dotStatusBad : styles.dotStatusGood);
    }, [summary, summaryRequest]);

    if (currentPath.includes('/app-devices')) {
        currentPath = '/app-devices/:device';
    }
    const isInPublicPath = publicPaths.map(path => `/${path}`).includes(currentPath);
    if (isInPublicPath) return <div />;

    const navigationItems: NavigationItem[] = [
        {
            id: 'buildings',
            key: 'Buildings',
            path: `/${paths.buildings}`,
            title: 'Buildings',
            iconName: 'business',
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            excludedFeatureToggles: [FeatureToggleEnum.oldPartnerView],
            accessWithResourceFilter: true,
            requiredGroupTypes: [GroupType.business],
        },
        {
            id: 'status',
            key: 'Status',
            path: `/${paths.status}`,
            title: 'StatusPage.Header',
            iconName: 'wifi_off',
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            excludedFeatureToggles: [FeatureToggleEnum.oldPartnerView],
            accessWithResourceFilter: true,
            extraClass,
            requiredGroupTypes: [GroupType.business],
        },
        {
            id: 'reports',
            key: 'Reports',
            path: `/${paths.reports}`,
            title: 'Reports.Reports',
            iconName: 'assignment',
            requiredRoleLevel: roleRestrictions.reports,
            excludedFeatureToggles: [FeatureToggleEnum.oldPartnerView],
            accessWithResourceFilter: true,
            requiredGroupTypes: [GroupType.business],
        },
        {
            id: 'dashboard',
            key: 'Dashboard',
            path: '/dashboard',
            title: 'Dashboard',
            iconName: 'dashboard',
            requiredRoleLevel: roleRestrictions.dashboardTiles,
            excludedFeatureToggles: [FeatureToggleEnum.oldPartnerView],
            accessWithResourceFilter: true,
            requiredGroupTypes: [GroupType.business],
        },
        {
            id: 'alerts',
            key: 'Alerts',
            path: `/${paths.alerts}`,
            title: 'NotificationAlerts.Alerts',
            iconName: 'notifications_active',
            requiredRoleLevel: roleRestrictions.alerts,
            excludedFeatureToggles: [FeatureToggleEnum.oldPartnerView],
            accessWithResourceFilter: false,
            requiredGroupTypes: [GroupType.business],
        },
        {
            id: 'partnerPage',
            key: 'Customers',
            path: `/${paths.partnerPage}`,
            title: 'Partner.Customers',
            iconName: 'people_outline',
            requiredRoleLevel: roleRestrictions.partner,
            accessWithResourceFilter: true,
            requiredGroupTypes: [GroupType.partner],
        },
        {
            id: 'integrations',
            key: 'Integrations',
            path: `/${paths.integrations}`,
            title: 'Integrations.Integrations',
            iconName: 'widgets',
            requiredRoleLevel: roleRestrictions.thirdPartyIntegration,
            accessWithResourceFilter: false,
            requiredGroupTypes: [],
        },
        {
            id: 'settings',
            key: 'Settings',
            path: `/${paths.settings}`,
            title: 'Account',
            iconName: 'account_circle',
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            accessWithResourceFilter: true,
            requiredGroupTypes: [],
        },
        {
            id: 'analytics',
            key: 'Analytics',
            path: `/${paths.analytics}`,
            title: 'Analytics.AnalyticsBeta',
            iconName: 'monitoring',
            requiredRoleLevel: RequiredRoleLevel.ABOVE_ADMIN,
            accessWithResourceFilter: true,
            requiredGroupTypes: [GroupType.business, GroupType.partner],
            requiredFeatureToggles: [FeatureToggleEnum.quicksightIntegration],
        },
    ].filter(item => {
        const partnerOnlyPage = item.id === 'partnerPage';
        const orgIsPartner = selectedGroup && selectedGroup.groupType === GroupType.partner;
        return partnerOnlyPage ? orgIsPartner : true;
    });

    const accessibleNavigationItems: NavigationItem[] = filterMenuElements(
        navigationItems,
        selectedGroup?.groupType,
        selectedGroup?.role,
        (selectedGroup && selectedGroup.resourceFilterOn) || false,
        featureToggles
    );

    let displayedLogo = logo;
    let displayPartnerLogo = false;
    const partnerWithLogo = selectedGroup && selectedGroup.groupType === GroupType.partner && partnerLogo;
    if (partnerWithLogo) {
        displayedLogo = partnerWithLogo;
        displayPartnerLogo = true;
    } else if (managedByOrganizationDetails && selectedGroup) {
        const partnerBrandingOn = selectedGroup.partnerBranding;
        const managedByOrgLogo = managedByOrganizationDetails.logoImage;
        if (managedByOrgLogo && partnerBrandingOn) {
            displayedLogo = managedByOrgLogo;
            displayPartnerLogo = true;
        }
    }

    return (
        <NavigationSideBar
            navigationItems={accessibleNavigationItems}
            logo={displayedLogo}
            slimLogo={simpleLogo}
            styleClass="business-navigation"
            poweredByLogo={displayPartnerLogo ? AirthingsHorizontalLogo : undefined}
        />
    );
};

export default NavigationComponent;
